import { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, ButtonGroup, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';
import {
  createHeroBanner,
  deleteHeroBanner,
  listHeroBanners,
  reorderHeroBanners,
  toggleHeroBanner,
  updateHeroBanner,
} from '../api/heroBannersApi';
import { apiGet, apiPost } from '../../services/apiClient';

const TYPE_OPTIONS = [
  { value: 'primary', label: 'Primary' },
  { value: 'secondary', label: 'Secondary' },
  { value: 'partitioned', label: 'Partitioned' },
];

const ALIGN_OPTIONS = ['left', 'center', 'right'];
const HEADING_SIZES = ['H1', 'H2', 'H3'];
const FONT_WEIGHTS = [400, 500, 600, 700, 800];
const IMAGE_FITS = [
  { value: 'cover', label: 'Cover (crop to fill)' },
  { value: 'contain', label: 'Contain (show full image)' },
];

const EMPTY_FORM = {
  type: 'primary',
  status: 'draft',
  sort_order: 0,
  badge_text: '',
  heading: '',
  subheading: '',
  primary_cta_text: '',
  primary_cta_link: '',
  secondary_cta_text: '',
  secondary_cta_link: '',
  alignment: 'left',
  text_color: '#111827',
  heading_size: 'H1',
  font_weight: 800,
  layout_split: '60-40',
  banner_height: 400,
  image_fit: 'cover',
  image_url: '',
};

const typeLabel = (value) => TYPE_OPTIONS.find((t) => t.value === value)?.label || value;

const sortByOrder = (a, b) => (a.sort_order || 0) - (b.sort_order || 0);

export default function HeroBannersManager({ token }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const [heroLimit, setHeroLimit] = useState(8);
  const [limitLoading, setLimitLoading] = useState(false);
  const [limitSaving, setLimitSaving] = useState(false);
  const [limitError, setLimitError] = useState('');
  const [showLimitManager, setShowLimitManager] = useState(true);

  const previewUrl = useMemo(() => {
    if (file) return URL.createObjectURL(file);
    return resolveAssetUrl(form.image_url);
  }, [file, form.image_url]);

  useEffect(() => {
    return () => {
      if (file) {
        try {
          URL.revokeObjectURL(previewUrl);
        } catch (_) {
          // ignore
        }
      }
    };
  }, [file, previewUrl]);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const rows = await listHeroBanners(token);
      setItems(Array.isArray(rows) ? rows : rows?.value || []);
    } catch (err) {
      setItems([]);
      setError(err.message || 'Failed to load hero banners.');
    } finally {
      setLoading(false);
    }
  };

  const loadHeroLimit = async () => {
    setLimitLoading(true);
    setLimitError('');
    try {
      const res = await apiGet('/api/admin/settings/system?key=home_hero_banner_limit', token);
      const rawValue = res?.value ?? res?.data?.value ?? res?.settings?.home_hero_banner_limit;
      const parsed = rawValue === null || rawValue === undefined || rawValue === '' ? 8 : Number(rawValue);
      setHeroLimit(Number.isFinite(parsed) ? parsed : 8);
    } catch (err) {
      if (err?.status === 403) {
        // Some admin roles can't access system settings. Hide this optional UI so the page still works.
        setShowLimitManager(false);
        setLimitError('');
      } else {
        setLimitError(err.message || 'Failed to load hero banner limit.');
      }
    } finally {
      setLimitLoading(false);
    }
  };

  const saveHeroLimit = async () => {
    const numeric = Number(heroLimit);
    if (!Number.isFinite(numeric) || numeric < 1 || numeric > 12) {
      setLimitError('Hero banner limit must be a number between 1 and 12.');
      return;
    }
    setLimitSaving(true);
    setLimitError('');
    try {
      await apiPost('/api/admin/settings/system', { key: 'home_hero_banner_limit', value: numeric }, token);
    } catch (err) {
      if (err?.status === 403) {
        setShowLimitManager(false);
        setLimitError('');
      } else {
        setLimitError(err.message || 'Failed to save hero banner limit.');
      }
    } finally {
      setLimitSaving(false);
    }
  };

  useEffect(() => {
    load();
    loadHeroLimit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const resetForm = () => {
    setEditing(null);
    setFile(null);
    setForm(EMPTY_FORM);
  };

  const onEdit = (row) => {
    setEditing(row);
    setFile(null);
    setForm({
      type: row.type || 'primary',
      status: row.status || 'draft',
      sort_order: Number(row.sort_order ?? 0),
      badge_text: row.badge_text || '',
      heading: row.heading || '',
      subheading: row.subheading || '',
      primary_cta_text: row.primary_cta_text || '',
      primary_cta_link: row.primary_cta_link || '',
      secondary_cta_text: row.secondary_cta_text || '',
      secondary_cta_link: row.secondary_cta_link || '',
      alignment: row.alignment || 'left',
      text_color: row.text_color || '#111827',
      heading_size: row.heading_size || 'H1',
      font_weight: Number(row.font_weight ?? 800),
      layout_split: row.layout_split || '60-40',
      banner_height: Number(row.banner_height ?? 400),
      image_fit: row.image_fit || 'cover',
      image_url: row.image_url || '',
    });
  };

  const onToggle = async (row) => {
    setError('');
    try {
      const nextStatus = row.status === 'published' ? 'draft' : 'published';
      await toggleHeroBanner(row.id, nextStatus, token);
      await load();
    } catch (err) {
      setError(err.message || 'Unable to update status.');
    }
  };

  const onDelete = async (row) => {
    // eslint-disable-next-line no-alert
    const ok = window.confirm('Delete this hero banner?');
    if (!ok) return;
    setError('');
    try {
      await deleteHeroBanner(row.id, token);
      setItems((prev) => prev.filter((item) => item.id !== row.id));
      if (editing?.id === row.id) resetForm();
    } catch (err) {
      setError(err.message || 'Unable to delete banner.');
    }
  };

  const moveInList = async (row, direction) => {
    const list = [...items].sort(sortByOrder);
    const idx = list.findIndex((item) => item.id === row.id);
    const nextIdx = idx + direction;
    if (idx < 0 || nextIdx < 0 || nextIdx >= list.length) return;
    const a = list[idx];
    const b = list[nextIdx];
    try {
      await reorderHeroBanners(
        [
          { id: a.id, sort_order: b.sort_order },
          { id: b.id, sort_order: a.sort_order },
        ],
        token
      );
      await load();
    } catch (err) {
      setError(err.message || 'Unable to reorder.');
    }
  };

  const onSave = async () => {
    setError('');
    if (!String(form.heading || '').trim()) {
      setError('Heading is required.');
      return;
    }
    if (!editing && !file) {
      setError('Image is required.');
      return;
    }

    const payload = {
      type: form.type,
      status: form.status,
      sort_order: Number(form.sort_order ?? 0),
      badge_text: form.badge_text,
      heading: form.heading,
      subheading: form.subheading,
      primary_cta_text: form.primary_cta_text,
      primary_cta_link: form.primary_cta_link,
      secondary_cta_text: form.secondary_cta_text,
      secondary_cta_link: form.secondary_cta_link,
      alignment: form.alignment,
      text_color: form.text_color,
      heading_size: form.heading_size,
      font_weight: Number(form.font_weight ?? 800),
      layout_split: form.layout_split,
      banner_height: Number(form.banner_height ?? 400),
      image_fit: form.image_fit || 'cover',
    };

    setSaving(true);
    try {
      if (editing?.id) {
        await updateHeroBanner(editing.id, payload, file, token);
      } else {
        await createHeroBanner(payload, file, token);
      }
      resetForm();
      await load();
    } catch (err) {
      setError(err.message || 'Unable to save banner.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-3 p-md-4">
      {showLimitManager ? (
        <Card className="mb-3">
          <Card.Body>
            <div className="d-flex flex-wrap align-items-end gap-2">
              <div className="me-auto">
                <div className="fw-semibold">Hero slider limit</div>
                <div className="text-muted small">Controls how many hero banners rotate on homepage.</div>
              </div>
              <Form.Group>
                <Form.Label className="small text-muted mb-1">Limit</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  max="12"
                  value={heroLimit}
                  onChange={(e) => setHeroLimit(e.target.value === '' ? '' : Number(e.target.value))}
                  style={{ maxWidth: 120 }}
                  disabled={limitLoading}
                />
              </Form.Group>
              <Button variant="outline-primary" onClick={saveHeroLimit} disabled={limitLoading || limitSaving}>
                {limitSaving ? 'Saving...' : 'Save limit'}
              </Button>
            </div>
            {limitError ? <Alert variant="danger" className="mt-3 mb-0">{limitError}</Alert> : null}
          </Card.Body>
        </Card>
      ) : null}

      {error ? (
        <Alert variant="danger" className="py-2">
          {error}
        </Alert>
      ) : null}

      <Row className="g-3">
        <Col lg={5}>
          <Card>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <div className="fw-semibold">{editing ? 'Edit banner' : 'Add banner'}</div>
                  <div className="text-muted small">Primary / Secondary / Partitioned, all in one hero slider.</div>
                </div>
                {editing ? (
                  <Button size="sm" variant="outline-secondary" onClick={resetForm}>
                    Cancel
                  </Button>
                ) : null}
              </div>

              <Form className="mt-3" onSubmit={(e) => e.preventDefault()}>
                <Row className="g-2">
                  <Col md={6}>
                    <Form.Label className="small text-muted mb-1">Hero type</Form.Label>
                    <Form.Select
                      value={form.type}
                      onChange={(e) => setForm((p) => ({ ...p, type: e.target.value }))}
                    >
                      {TYPE_OPTIONS.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={6}>
                    <Form.Label className="small text-muted mb-1">Status</Form.Label>
                    <Form.Select value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}>
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                    </Form.Select>
                  </Col>

                  <Col md={12}>
                    <Form.Label className="small text-muted mb-1">Badge text (optional)</Form.Label>
                    <Form.Control value={form.badge_text} onChange={(e) => setForm((p) => ({ ...p, badge_text: e.target.value }))} />
                  </Col>
                  <Col md={12}>
                    <Form.Label className="small text-muted mb-1">Heading</Form.Label>
                    <Form.Control value={form.heading} onChange={(e) => setForm((p) => ({ ...p, heading: e.target.value }))} />
                  </Col>
                  <Col md={12}>
                    <Form.Label className="small text-muted mb-1">Subheading</Form.Label>
                    <Form.Control value={form.subheading} onChange={(e) => setForm((p) => ({ ...p, subheading: e.target.value }))} />
                  </Col>

                  <Col md={6}>
                    <Form.Label className="small text-muted mb-1">Primary CTA text</Form.Label>
                    <Form.Control value={form.primary_cta_text} onChange={(e) => setForm((p) => ({ ...p, primary_cta_text: e.target.value }))} />
                  </Col>
                  <Col md={6}>
                    <Form.Label className="small text-muted mb-1">Primary CTA link</Form.Label>
                    <Form.Control value={form.primary_cta_link} onChange={(e) => setForm((p) => ({ ...p, primary_cta_link: e.target.value }))} placeholder="/stays or https://..." />
                  </Col>

                  <Col md={6}>
                    <Form.Label className="small text-muted mb-1">Secondary CTA text</Form.Label>
                    <Form.Control value={form.secondary_cta_text} onChange={(e) => setForm((p) => ({ ...p, secondary_cta_text: e.target.value }))} />
                  </Col>
                  <Col md={6}>
                    <Form.Label className="small text-muted mb-1">Secondary CTA link</Form.Label>
                    <Form.Control value={form.secondary_cta_link} onChange={(e) => setForm((p) => ({ ...p, secondary_cta_link: e.target.value }))} placeholder="/packages or https://..." />
                  </Col>

                  <Col md={4}>
                    <Form.Label className="small text-muted mb-1">Alignment</Form.Label>
                    <Form.Select value={form.alignment} onChange={(e) => setForm((p) => ({ ...p, alignment: e.target.value }))}>
                      {ALIGN_OPTIONS.map((v) => (
                        <option key={v} value={v}>
                          {v}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={4}>
                    <Form.Label className="small text-muted mb-1">Heading size</Form.Label>
                    <Form.Select value={form.heading_size} onChange={(e) => setForm((p) => ({ ...p, heading_size: e.target.value }))}>
                      {HEADING_SIZES.map((v) => (
                        <option key={v} value={v}>
                          {v}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={4}>
                    <Form.Label className="small text-muted mb-1">Font weight</Form.Label>
                    <Form.Select value={Number(form.font_weight || 800)} onChange={(e) => setForm((p) => ({ ...p, font_weight: Number(e.target.value) }))}>
                      {FONT_WEIGHTS.map((v) => (
                        <option key={v} value={v}>
                          {v}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>

                  <Col md={6}>
                    <Form.Label className="small text-muted mb-1">Text color (optional)</Form.Label>
                    <Form.Control value={form.text_color || ''} onChange={(e) => setForm((p) => ({ ...p, text_color: e.target.value }))} placeholder="#111827" />
                  </Col>
                  <Col md={3}>
                    <Form.Label className="small text-muted mb-1">Layout split</Form.Label>
                    <Form.Control value={form.layout_split} disabled />
                  </Col>
                  <Col md={3}>
                    <Form.Label className="small text-muted mb-1">Banner height</Form.Label>
                    <Form.Control type="number" min="320" value={Number(form.banner_height ?? 400)} onChange={(e) => setForm((p) => ({ ...p, banner_height: Number(e.target.value || 400) }))} />
                  </Col>
                  <Col md={6}>
                    <Form.Label className="small text-muted mb-1">Image fit</Form.Label>
                    <Form.Select value={form.image_fit || 'cover'} onChange={(e) => setForm((p) => ({ ...p, image_fit: e.target.value }))}>
                      {IMAGE_FITS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>

                  <Col md={12}>
                    <Form.Label className="small text-muted mb-1">Image</Form.Label>
                    <Form.Control
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={(e) => setFile(e.target.files?.[0] || null)}
                    />
                    <div className="text-muted small mt-1">Recommended sizes: Desktop 1600x900, Mobile 1080x1350</div>
                    {previewUrl ? (
                      <div className="mt-2">
                        <img
                          src={previewUrl}
                          alt="Preview"
                          style={{
                            width: '100%',
                            maxHeight: 220,
                            objectFit: form.image_fit === 'contain' ? 'contain' : 'cover',
                            borderRadius: 12,
                            border: '1px solid rgba(0,0,0,0.08)',
                            background: '#f8fafc',
                          }}
                        />
                      </div>
                    ) : null}
                    <div className="mt-3 rounded-3xl border border-slate-200 bg-white p-3 text-center shadow-sm">
                      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: 160 }}>
                        {previewUrl ? (
                          <img
                            src={previewUrl}
                            alt="Hero banner preview"
                            style={{
                              width: '100%',
                              maxHeight: 140,
                              objectFit: form.image_fit === 'contain' ? 'contain' : 'cover',
                            }}
                          />
                        ) : (
                          <div className="text-muted small">Preview appears here</div>
                        )}
                      </div>
                      {form.badge_text ? (
                        <div className="mt-2 text-uppercase text-muted small" style={{ letterSpacing: '0.2em' }}>
                          {form.badge_text}
                        </div>
                      ) : null}
                      <div className="fw-semibold mt-1">{form.heading || 'Banner heading'}</div>
                      {form.subheading ? <div className="text-muted small">{form.subheading}</div> : null}
                    </div>
                  </Col>

                  <Col md={12} className="d-flex gap-2 pt-2">
                    <Button variant="primary" onClick={onSave} disabled={saving}>
                      {saving ? (
                        <>
                          <Spinner size="sm" className="me-2" /> Saving
                        </>
                      ) : (
                        editing ? 'Update' : 'Create'
                      )}
                    </Button>
                    <Button variant="outline-secondary" onClick={resetForm} disabled={saving}>
                      Reset
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={7}>
          <Card>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start mb-2">
                <div>
                  <div className="fw-semibold">Hero slider sequence</div>
                  <div className="text-muted small">Reorder with Up/Down. Status controls publish/draft.</div>
                </div>
              </div>

              <Table responsive bordered size="sm" className="mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: 90 }}>Image</th>
                    <th>Heading</th>
                    <th style={{ width: 130 }}>Type</th>
                    <th style={{ width: 110 }}>Status</th>
                    <th style={{ width: 70 }}>Sort</th>
                    <th style={{ width: 260 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {[...items].sort(sortByOrder).map((row, idx) => (
                    <tr key={row.id}>
                      <td>
                        {row.image_url ? (
                          <div>
                            <img
                              src={resolveAssetUrl(row.image_url)}
                              alt={row.heading || 'Banner'}
                              style={{ width: 70, height: 46, objectFit: 'cover', borderRadius: 6 }}
                              loading="lazy"
                            />
                            {row.imageWidth && row.imageHeight ? (
                              <div className="text-muted small mt-1">
                                {row.imageWidth}x{row.imageHeight}
                              </div>
                            ) : null}
                          </div>
                        ) : (
                          <span className="text-muted small">No image</span>
                        )}
                      </td>
                      <td className="text-truncate" style={{ maxWidth: 300 }}>{row.heading || '(Untitled)'}</td>
                      <td>
                        <Badge bg="light" text="dark" className="border">
                          {typeLabel(row.type)}
                        </Badge>
                      </td>
                      <td>{row.status === 'published' ? <Badge bg="success">Published</Badge> : <Badge bg="secondary">Draft</Badge>}</td>
                      <td>{Number(row.sort_order ?? 0)}</td>
                      <td className="text-nowrap">
                        <Button size="sm" variant="outline-primary" onClick={() => onEdit(row)}>
                          Edit
                        </Button>{' '}
                        <Button size="sm" variant="outline-secondary" onClick={() => onToggle(row)}>
                          Toggle
                        </Button>{' '}
                        <ButtonGroup size="sm">
                          <Button variant="outline-secondary" onClick={() => moveInList(row, -1)} disabled={idx === 0}>
                            Up
                          </Button>
                          <Button variant="outline-secondary" onClick={() => moveInList(row, 1)} disabled={idx === items.length - 1}>
                            Down
                          </Button>
                        </ButtonGroup>{' '}
                        <Button size="sm" variant="outline-danger" onClick={() => onDelete(row)}>
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {!loading && items.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center text-muted">
                        No hero banners yet.
                      </td>
                    </tr>
                  ) : null}
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="text-center text-muted">
                        <Spinner size="sm" className="me-2" /> Loading...
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
